const API_BASE = 'https://www.googleapis.com/youtube/v3';
const API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY;
const CHANNEL_HANDLE = 'daddacool';
const RELEASE_LIMIT = 8;
const UPLOAD_SCAN_LIMIT = 50;
const MUSIC_CATEGORY_ID = '10';
const MIN_RELEASE_SECONDS = 61;

let releasesRequest;

async function youtubeRequest(resource, parameters) {
  if (!API_KEY) {
    throw new Error('YouTube API key is not configured');
  }

  const query = new URLSearchParams({
    ...parameters,
    key: API_KEY
  });
  const response = await fetch(`${API_BASE}/${resource}?${query}`);

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const message =
      payload.error?.message || 'Unable to load YouTube releases';
    throw new Error(message);
  }

  return response.json();
}

function durationToSeconds(duration = '') {
  const match = duration.match(
    /^P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/
  );

  if (!match) return 0;

  const [, days = 0, hours = 0, minutes = 0, seconds = 0] = match;

  return (
    Number(days) * 86400 +
    Number(hours) * 3600 +
    Number(minutes) * 60 +
    Number(seconds)
  );
}

function hasShortMarker(video) {
  const snippet = video.snippet || {};
  const text =
    `${snippet.title || ''} ${snippet.description || ''}`.toLowerCase();

  return (
    /(^|\s)#shorts?\b/.test(text) ||
    /\byoutube shorts?\b/.test(text) ||
    /\bshort-form\b/.test(text)
  );
}

function isMusicRelease(video) {
  const duration = durationToSeconds(video.contentDetails?.duration);

  return (
    video.status?.privacyStatus === 'public' &&
    video.snippet?.categoryId === MUSIC_CATEGORY_ID &&
    duration >= MIN_RELEASE_SECONDS &&
    !hasShortMarker(video)
  );
}

async function getChannel() {
  const data = await youtubeRequest('channels', {
    part: 'id,snippet,contentDetails',
    forHandle: CHANNEL_HANDLE
  });
  const channel = data.items?.[0];

  if (!channel) {
    throw new Error(`YouTube channel @${CHANNEL_HANDLE} could not be found`);
  }

  return channel;
}

async function getUploadCandidates(playlistId) {
  const data = await youtubeRequest('playlistItems', {
    part: 'contentDetails',
    playlistId,
    maxResults: String(UPLOAD_SCAN_LIMIT)
  });

  return (data.items || [])
    .map((item) => item.contentDetails?.videoId)
    .filter(Boolean);
}

async function getVideoDetails(videoIds) {
  if (!videoIds.length) return [];

  const data = await youtubeRequest('videos', {
    part: 'snippet,contentDetails,status',
    id: videoIds.join(',')
  });

  return data.items || [];
}

function mapRelease(video, index) {
  const snippet = video.snippet || {};
  const videoId = video.id;

  return {
    video_id: videoId,
    title: snippet.title || 'Dadda Cool release',
    artist: snippet.channelTitle || 'Dadda Cool',
    description: snippet.description || '',
    url: `https://www.youtube.com/watch?v=${videoId}`,
    image_url:
      snippet.thumbnails?.maxres?.url ||
      snippet.thumbnails?.standard?.url ||
      snippet.thumbnails?.high?.url ||
      snippet.thumbnails?.medium?.url ||
      `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    platform: 'YouTube',
    order: index + 1,
    published_at: snippet.publishedAt || ''
  };
}

async function loadLatestYouTubeReleases() {
  const channel = await getChannel();
  const playlistId = channel.contentDetails?.relatedPlaylists?.uploads;

  if (!playlistId) {
    throw new Error(`Uploads for @${CHANNEL_HANDLE} could not be found`);
  }

  const videoIds = await getUploadCandidates(playlistId);
  const videos = await getVideoDetails(videoIds);
  const videoById = new Map(
    videos.map((video) => [video.id, video])
  );

  return videoIds
    .map((videoId) => videoById.get(videoId))
    .filter(Boolean)
    .filter(isMusicRelease)
    .slice(0, RELEASE_LIMIT)
    .map(mapRelease);
}

export function getLatestYouTubeReleases({ force = false } = {}) {
  if (!releasesRequest || force) {
    releasesRequest = loadLatestYouTubeReleases().catch((error) => {
      releasesRequest = undefined;
      throw error;
    });
  }

  return releasesRequest;
}

export const YOUTUBE_RELEASES_URL =
  'https://www.youtube.com/@daddacool/releases';